import { createContext, useContext } from 'react';

import type { DiscoveryButton, DiscoveryIntent, Message } from '../discovery/types';
import type { FileUploadView } from './FileUploadView';

export type { DiscoveryButton, DiscoveryIntent } from '../discovery/types';
export type ChatMessage = Message;

/**
 * Everything the "Chat with Dooglie" view reads and can ask for.
 *
 * `useConversation` fills it from the backend; the onboarding tour can fill it
 * from a script. The views (`ChatView`, `MessageList`, `MessageInput`) import
 * nothing from the API or the store.
 */
export interface ChatData {
  /** Newest first. */
  messages: ChatMessage[];
  /** A turn is in flight. */
  loading: boolean;
  /** The history has not arrived yet. */
  initialLoading: boolean;
  /** The prompt buttons by the text box. */
  buttons: DiscoveryButton[];
  /**
   * Sends a turn: typed text (with whatever is attached), or a button's intent
   * with empty text. `intent` is passed through to `/discovery_chat`.
   * `prompt` is the question the view itself showed the user, when it asked
   * for one subject: the backend stores the text as the answer to that, on a
   * turn of its own, instead of to whatever Dooglie last asked.
   */
  send(text: string, intent?: DiscoveryIntent, prompt?: string): Promise<void>;
  upload: FileUploadView;
  /** Markdown to sanitised HTML. */
  renderMarkdown(md: string): string;
}

const ChatDataContext = createContext<ChatData | null>(null);

/** `<ChatDataProvider value={chatData}>` around a `ChatView`. */
export const ChatDataProvider = ChatDataContext.Provider;

export function useChatData(): ChatData {
  const data = useContext(ChatDataContext);
  if (!data) throw new Error('useChatData needs a <ChatDataProvider> above it');
  return data;
}
